import React, {useEffect, useState} from 'react';
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  Divider,
  MenuItem,
  Select,
} from '@material-ui/core';
// import Switch from '@material-ui/core/Switch';
import {v4 as uuidv4} from 'uuid';

import {CheckoutButtons} from './capabilities';
import {APP_ENV, GUEST, X_CUSTOMER_ID, X_MERCHANT_ID} from './utils/constants';
import {setHeaders} from './api/apis';
import getMaterialUiTheme from './style/material-ui/get-material-ui-theme';

const getMerchantTransactionId = () => uuidv4();

const merchantIdMap = {
  test: 'a49a5ea7-3f98-4205-88ac-b76ffbf24e54',
  stage: 'ab27babe-e649-4749-a51f-765dbadbce98',
  prod: '',
};

const getMerchantId = (appEnv) => {
  return merchantIdMap[appEnv];
};

type AppEnvValue = 'test' | 'stage' | 'prod';

const AppTestHarness = () => {
  const randomInt = Math.floor(Math.random() * 10000);

  const [state, setState] = useState<{
    appEnv: AppEnvValue;
    patientName: string;
    mrn: string;
    amount: string;
    amountInt: number;
    message: string;
    response: string;
    merchantId: string;
    merchantTransactionId: string;
    statementDescriptorSuffix: string;
    hsid: string;
    phoneNumber: string;
  }>({
    appEnv: 'stage',
    patientName: '',
    mrn: '',
    amount: (randomInt / 100).toString(),
    amountInt: randomInt,
    message: '',
    response: '',
    merchantId: '',
    merchantTransactionId: getMerchantTransactionId(),
    statementDescriptorSuffix: '',
    hsid: '',
    phoneNumber: '',
  });

  const [openAppEnv, setOpenAppEnv] = useState(false);

  const openAppEnvMenu = () => {
    setOpenAppEnv(true);
  };

  const closeAppEnvMenu = () => {
    setOpenAppEnv(false);
  };

  // const [shouldShowHealthSafePayButton, setShouldShowHealthSafePayButton] = useState<boolean>(true);

  // const toggleCheckoutOption = () => {
  //   setShouldShowHealthSafePayButton((val) => !val);
  // };

  const onSuccess = (result: CheckoutSessionsResponse) => {
    setState({
      ...state,
      message: 'Payment Successful',
      response: JSON.stringify(result, null, 2),
    });
  };
  const onFailure = (result: CheckoutSessionsResponse) => {
    setState({
      ...state,
      message: 'Payment Failed',
      response: JSON.stringify(result, null, 2),
    });
  };
  const onCancel = (result?: CheckoutSessionsResponse) => {
    setState({...state, message: 'Payment Link', response: JSON.stringify(result, null, 2), merchantTransactionId: getMerchantTransactionId()});
  };
  const onInitializeError = (message) => {
    setState({...state, message: 'Initialization Error', response: message});
  };

  const configurePublicAPI = () => {
    setHeaders({
      [X_CUSTOMER_ID]: GUEST,
      [X_MERCHANT_ID]: state.merchantId,
    });
  };

  const setAppEnvAndReload = (appEnv: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('appEnv', appEnv);
    window.location.search = searchParams.toString();
  };

  const getAppEnv = () => {
    let appEnv = new URL(window.location.href).searchParams.get('appEnv');
    if (!appEnv) {
      const env = window.location.host.replace('wallet.healthsafepay.com', '');

      if (env.startsWith('dev') || env.startsWith('localhost')) {
        appEnv = APP_ENV.test;
      } else if (env.startsWith('stg')) {
        appEnv = APP_ENV.stage;
      } else if (env === '') {
        appEnv = APP_ENV.prod;
      }
    }
    return appEnv as AppEnvValue;
  };

  useEffect(() => {
    configurePublicAPI();
  }, [state.merchantId]);

  useEffect(() => {
    const env = getAppEnv();
    setState((stateData) => {
      return {
        ...stateData,
        appEnv: env,
        merchantId: getMerchantId(env),
      };
    });
  }, []);
  return (
    <ThemeProvider theme={getMaterialUiTheme()}>
      <Grid container justifyContent="center" direction="column" alignItems="center">
        <Grid item xs={12}>
          <Box maxWidth="400px">
            <Grid container direction="column" style={{gap: '8px'}}>
              <h2>Configuration</h2>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>App Environment</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Select
                    id="appEnv"
                    open={openAppEnv}
                    onClose={closeAppEnvMenu}
                    onOpen={openAppEnvMenu}
                    value={state.appEnv}
                    onChange={(e) => {
                      setAppEnvAndReload(e.target.value as string);
                    }}
                  >
                    <MenuItem value="test">test</MenuItem>
                    <MenuItem value="stage">stage</MenuItem>
                    <MenuItem value="prod">prod</MenuItem>
                  </Select>
                </Grid>
              </Grid>
              <h2>Payment Information</h2>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>Patient Name</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField onChange={(e) => setState({...state, patientName: e.target.value})} />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>Account Number (MRN)</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField onChange={(e) => setState({...state, mrn: e.target.value})} />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>Payment Amount</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    value={state.amount}
                    onChange={(e) => {
                      let amountInt = 0;
                      try {
                        amountInt = Math.round(parseFloat(e.target.value) * 100);
                      } catch {
                        // error will default amount to 0
                      }
                      setState({...state, amount: e.target.value, amountInt});
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>Statement Descriptor</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    value={state.statementDescriptorSuffix}
                    onChange={(e) => {
                      setState({...state, statementDescriptorSuffix: e.target.value});
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <h2>Merchant</h2>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography>Merchant ID</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      onChange={(e) => setState({...state, merchantId: e.target.value})}
                      value={state.merchantId}
                    />
                  </Grid>
                </Grid>
                <h2>Customer Information</h2>
                <Grid container style={{alignItems: 'center'}}>
                  <Grid item xs={6}>
                    <Typography>Healthsafe ID (UUID)</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      onChange={(e) => setState({...state, hsid: e.target.value})}
                      value={state.hsid}
                    />
                  </Grid>
                </Grid>
                <Grid container style={{alignItems: 'center'}}>
                  <Grid item xs={6}>
                    <Typography>Mobile Number</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      onChange={(e) => setState({...state, phoneNumber: e.target.value})}
                    />
                    {/* <Switch
                      checked={shouldShowHealthSafePayButton}
                      onChange={toggleCheckoutOption}
                      color="primary"
                      name="checkedB"
                      inputProps={{'aria-label': 'Pay with HealthSafe Pay'}}
                    /> */}
                  </Grid>
                </Grid>
              </Grid>

              <Box marginTop={10}>
                <CheckoutButtons
                  shouldShowHealthSafePayButton={true}
                  merchantId={state.merchantId}
                  merchantTransactionId={state.merchantTransactionId}
                  statementDescriptorSuffix={state.statementDescriptorSuffix}
                  onSuccess={onSuccess}
                  onFailure={onFailure}
                  onCancel={onCancel}
                  onInitializeError={onInitializeError}
                  amount={state.amountInt}
                  hsid={state.hsid}
                  phoneNumber={state.phoneNumber}
                  metadata={{
                    mrn: state.mrn,
                  }}
                  appEnv={state.appEnv}
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box marginTop={10} minWidth="400px" sx={{p: '8px'}} style={{overflow: 'hidden'}}>
            <Typography>Progress: {!state.message ? 'Pending' : null}</Typography>

            <Box marginTop={1} marginBottom={3}>
              <Divider />
            </Box>

            <Typography>{state.message}</Typography>

            <Box marginTop={2}>
              <pre
                style={{
                  backgroundColor: '#fafafa',
                  color: '#5a5a5a',
                  overflowX: 'auto',
                  whiteSpace: 'pre',
                  wordWrap: 'normal',
                }}
              >
                {state.response}
              </pre>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default AppTestHarness;
